const SELECTORS = {
  modeSelf: "[data-gcrf-mode-self]",
  modeRecipient: "[data-gcrf-mode-recipient]",
  fields: "[data-gcrf-fields]",
  email: "[data-gcrf-email]",
  name: "[data-gcrf-name]",
  message: "[data-gcrf-message]",
  sendOn: "[data-gcrf-send-on]",
  characterCount: "[data-gcrf-character-count]",
  timezoneOffset: "[data-gcrf-timezone-offset]",
  liveRegion: "[data-gcrf-live-region]",
};

const SHOPIFY_RECIPIENT_FLAG_NAME =
  "properties[__shopify_send_gift_card_to_recipient]";

const SEND_ON_MAX_DAYS = 90;

class GiftCardRecipientForm extends HTMLElement {
  #fieldInputs = [];
  #recipientFlagInput = null;

  connectedCallback() {
    try {
      this.init();
    } catch (error) {
      console.error("[GiftCardRecipientForm:init]", error);
    }
  }

  init() {
    this.#bindElements();
    this.#bindEvents();
    this.#applyDateConstraints();
    this.#enterSelfMode();
  }

  #bindElements() {
    this.modeSelfInput = this.querySelector(SELECTORS.modeSelf);
    this.modeRecipientInput = this.querySelector(SELECTORS.modeRecipient);
    this.fieldsWrapper = this.querySelector(SELECTORS.fields);
    this.emailInput = this.querySelector(SELECTORS.email);
    this.nameInput = this.querySelector(SELECTORS.name);
    this.messageInput = this.querySelector(SELECTORS.message);
    this.sendOnInput = this.querySelector(SELECTORS.sendOn);
    this.characterCountEl = this.querySelector(SELECTORS.characterCount);
    this.timezoneOffsetInput = this.querySelector(SELECTORS.timezoneOffset);
    this.liveRegion = this.querySelector(SELECTORS.liveRegion);

    this.#fieldInputs = [
      this.emailInput,
      this.nameInput,
      this.messageInput,
      this.sendOnInput,
    ].filter(Boolean);
  }

  #bindEvents() {
    this.modeSelfInput?.addEventListener(
      "change",
      this.#enterSelfMode.bind(this),
    );
    this.modeRecipientInput?.addEventListener(
      "change",
      this.#enterRecipientMode.bind(this),
    );
    this.messageInput?.addEventListener(
      "input",
      this.#updateCharacterCount.bind(this),
    );
  }

  #applyDateConstraints() {
    if (!this.sendOnInput) return;
    const today = new Date();
    const max = new Date();
    max.setDate(today.getDate() + SEND_ON_MAX_DAYS);
    this.sendOnInput.min = this.#formatDate(today);
    this.sendOnInput.max = this.#formatDate(max);
  }

  #formatDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  #enterSelfMode() {
    if (this.fieldsWrapper) this.fieldsWrapper.hidden = true;
    this.#fieldInputs.forEach((input) => {
      input.disabled = true;
      input.value = "";
    });
    this.#removeRecipientFlag();
    if (this.timezoneOffsetInput) {
      this.timezoneOffsetInput.disabled = true;
      this.timezoneOffsetInput.value = "";
    }
    this.#updateCharacterCount();
    this.#announce("fieldsHidden");
  }

  #enterRecipientMode() {
    if (this.fieldsWrapper) this.fieldsWrapper.hidden = false;
    this.#fieldInputs.forEach((input) => {
      input.disabled = false;
    });
    this.#ensureRecipientFlag();
    if (this.timezoneOffsetInput) {
      this.timezoneOffsetInput.disabled = false;
      this.timezoneOffsetInput.value = String(new Date().getTimezoneOffset());
    }
    this.emailInput?.focus();
    this.#announce("fieldsVisible");
  }

  #ensureRecipientFlag() {
    if (this.#recipientFlagInput && this.contains(this.#recipientFlagInput)) {
      return;
    }
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = SHOPIFY_RECIPIENT_FLAG_NAME;
    input.value = "on";
    this.appendChild(input);
    this.#recipientFlagInput = input;
  }

  #removeRecipientFlag() {
    if (this.#recipientFlagInput) {
      this.#recipientFlagInput.remove();
      this.#recipientFlagInput = null;
    }
  }

  #updateCharacterCount() {
    if (!this.characterCountEl || !this.messageInput) return;
    const template =
      this.characterCountEl.dataset.gcrfCharacterCountTemplate ||
      "[current] / [max]";
    const max =
      Number(this.characterCountEl.dataset.gcrfCharacterCountMax) ||
      this.messageInput.maxLength ||
      0;
    const current = this.messageInput.value.length;
    this.characterCountEl.textContent = template
      .replace("[current]", String(current))
      .replace("[max]", String(max));
  }

  #announce(key) {
    if (!this.liveRegion) return;
    const messages = {
      fieldsVisible:
        window.Theme?.translations?.gift_card_recipient_fields_visible ||
        "Recipient fields are now visible",
      fieldsHidden:
        window.Theme?.translations?.gift_card_recipient_fields_hidden ||
        "Recipient fields are now hidden",
    };
    this.liveRegion.textContent = messages[key] ?? "";
  }
}

if (!customElements.get("gift-card-recipient-form")) {
  customElements.define("gift-card-recipient-form", GiftCardRecipientForm);
}
